import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, Container, Grid, Paper, Chip, TextField, CircularProgress } from '@mui/material';
import { functions } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import pkg from '../../package.json';

// ── Ikoner ─────────────────────────────────────────────────────────────────────
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import GridViewIcon from '@mui/icons-material/GridView';
import GroupsIcon from '@mui/icons-material/Groups';
import GavelIcon from '@mui/icons-material/Gavel';
import PersonIcon from '@mui/icons-material/Person';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SchoolIcon from '@mui/icons-material/School';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import BusinessIcon from '@mui/icons-material/Business';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import TvIcon from '@mui/icons-material/Tv';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import MapIcon from '@mui/icons-material/Map';
import BarChartIcon from '@mui/icons-material/BarChart';
import SupportIcon from '@mui/icons-material/Support';
import WbIncandescentIcon from '@mui/icons-material/WbIncandescent';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import TimerIcon from '@mui/icons-material/Timer';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PollIcon from '@mui/icons-material/Poll';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import TrainIcon from '@mui/icons-material/Train';
import ArticleIcon from '@mui/icons-material/Article';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import InstallMobileIcon from '@mui/icons-material/InstallMobile';
import DescriptionIcon from '@mui/icons-material/Description';

const ORANGE = '#E05500';

// ── Logo ──────────────────────────────────────────────────────────────────────
function Logo({ size = '2rem' }) {
  return (
    <Typography fontWeight={900} sx={{
      fontSize: size,
      color: 'white',
      letterSpacing: '-0.5px',
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      lineHeight: 1,
    }}>
      Lagbas<Box component="span" sx={{ color: ORANGE }}>N</Box>
    </Typography>
  );
}

// ── Animated counter ──────────────────────────────────────────────────────────
function Counter({ to, suffix = '', duration = 1400 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      observer.disconnect();
      let start = null;
      const step = (ts) => {
        if (!start) start = ts;
        const p = Math.min((ts - start) / duration, 1);
        setVal(Math.round(p * to));
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, { threshold: 0.4 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [to, duration]);
  return <span ref={ref}>{val}{suffix}</span>;
}

// ── Module groups ─────────────────────────────────────────────────────────────
const MODULE_GROUPS = [
  {
    category: 'Vinna fler anbud',
    color: ORANGE,
    gradient: `linear-gradient(135deg, ${ORANGE}18 0%, transparent 60%)`,
    modules: [
      { icon: PersonIcon,    label: 'Konsultprofiler & CV',   desc: 'Strukturerade profiler med uppdrag, kompetenser och certifieringar. Skapa anbud-varianter per anbud — anpassa längd och innehåll utan att röra originalet. Exportera som professionell PDF med ett klick.' },
      { icon: AutoAwesomeIcon,label: 'AI-matchning',          desc: 'Ladda upp ett förfrågningsunderlag och låt Claude AI identifiera vilka konsulter som passar bäst. Matchning mot krav, gap-analys och automatisk profilering för varje specifikt anbud.' },
      { icon: GavelIcon,     label: 'Anbud & Anbudsprocess',  desc: 'Hantera hela anbudsprocessen — från inkommande lead till vunnet kontrakt. Vinststatistik, teamsammansättning, statusspårning och koppling mot CRM.' },
      { icon: MenuBookIcon,  label: 'Referensuppdrag',        desc: 'Skapa och hantera referensblad för anbud. Filtrera på typ, bransch och geografi. Koppla automatiskt till rätt konsulter och exportera anpassade referensbilagor.' },
      { icon: DescriptionIcon,label: 'Offerter',              desc: 'Generera offerttexter med AI baserat på kund och uppdragstyp. Hantera versioner, prismodeller och direktexport till kundanpassad PDF.' },
      { icon: QueryStatsIcon, label: 'Gap-analys',            desc: 'Jämför kravprofil mot tillgängliga konsulter automatiskt. Se exakt vilken kompetens som saknas och vilka profiler som behöver stärkas inför ett anbud.' },
    ],
  },
  {
    category: 'Workshoppa & Strategisera',
    color: '#42A5F5',
    gradient: 'linear-gradient(135deg, rgba(66,165,245,0.12) 0%, transparent 60%)',
    modules: [
      { icon: GroupsIcon,       label: 'WorkshopN',           desc: 'Frågebaserade workshops med live-svar via QR-kod. Ordmoln, AI-analys av svar, hybridblad för pappersworkshops och nedräkningstimer per fråga. Deltagarna svarar från mobilen — facilitatorn ser allt i realtid.' },
      { icon: GridViewIcon,     label: 'SwotN',               desc: 'Digital SWOT-analys med tre designade mallar (Classic 2×2, Kolumner, Vivid). Centrat SWOT-diamond, AI-strategianalys med korskopplingar, hybridpapper för handskrivna workshops och nedräkningstimer per kvadrant.' },
      { icon: TimerIcon,        label: 'Nedräkningstimers',   desc: 'Stor, tydlig fullskärms-timer med färgvarning (orange → röd). Tickande ljud de sista 10 sekunderna och alarmljud vid noll. Starta per fråga i WorkshopN eller per kvadrant i SwotN.' },
      { icon: QrCodeIcon,       label: 'QR & Delning',        desc: 'Generera QR-koder för snabb inbjudan till workshops och SWOT-sessioner. Deltagarna scannar och bidrar direkt — inga konton, inga appar, inga hinder.' },
      { icon: PollIcon,         label: 'Ordmoln & Analys',    desc: 'Live-ordmoln från deltagarnas svar. AI sammanfattar teman, mönster och förslag till åtgärder baserat på hela sessionens material.' },
      { icon: CloudUploadIcon,  label: 'Hybridläge',          desc: 'Skriv ut pappersblad för deltagare som skriver för hand. Skanna bladen efteråt — AI extraherar all text och importerar bidragen direkt in i det digitala boardet.' },
    ],
  },
  {
    category: 'Driva & Utveckla teamet',
    color: '#4CAF50',
    gradient: 'linear-gradient(135deg, rgba(76,175,80,0.1) 0%, transparent 60%)',
    modules: [
      { icon: PeopleIcon,             label: 'Medarbetarregister',   desc: 'Centralt register med import från Excel/CSV (SharePoint-format). Automatisk kolumnmappning, GDPR-radering och stöd för tidigare anställda utan systemkonto.' },
      { icon: SchoolIcon,             label: 'IUP & Kompetensutveckling', desc: 'Individuella utvecklingsplaner kopplade till varje medarbetare. Malleditor, teamöversikt och AI-assistans vid formulering av mål och åtgärder.' },
      { icon: AssignmentTurnedInIcon, label: 'Onboarding',           desc: 'Strukturerade onboarding-checklistor per roll. Spåra progress, tilldela uppgifter och säkerställ att varje ny medarbetare får rätt start.' },
      { icon: RecordVoiceOverIcon,    label: 'Rekrytering',          desc: 'Hantera kandidater, intervjuer och beslut i en samlad vy. Koppla rekrytering direkt mot befintliga kompetensgap och pågående anbud.' },
      { icon: SupportIcon,            label: 'HR',                   desc: 'Samlad HR-vy med anställningsuppgifter, kontraktshantering och behörighetsstyrning per roll — från superadmin till medarbetare.' },
      { icon: NotificationsActiveIcon,label: 'PlingKlang',           desc: 'Interna aviseringar och notifieringar. Firar vinster, påminner om deadlines och håller teamet uppdaterat i realtid.' },
      { icon: ViewWeekIcon,           label: 'FramtidN — Resursplan', desc: 'Visuell resursplanering per vecka med drag-select, budgetrad och projektvy. Se vem som är belagd, när kapacitet finns och planera framåt direkt i tidslinjen.' },
    ],
  },
  {
    category: 'Affärs- & projektprocess',
    color: '#FF9800',
    gradient: 'linear-gradient(135deg, rgba(255,152,0,0.1) 0%, transparent 60%)',
    modules: [
      { icon: BusinessIcon,    label: 'CRM & Leads',        desc: 'Hantera kunder, kontakter och affärsmöjligheter. Spåra leadsstatus, koppla anbud till företag och bygg upp en strukturerad säljpipeline.' },
      { icon: BarChartIcon,    label: 'Projekt',            desc: 'Aktivt projektregister med statusspårning, budgetöversikt och koppling mot anbud och team. Håll koll på hela projektportföljen.' },
      { icon: ArticleIcon,     label: 'Avtal',              desc: 'Hantera kundavtal och ramavtal. Koppla mot projekt och konsulter, spåra löptider och säkerställ att ingen viktig förnyelse missas.' },
      { icon: MapIcon,         label: 'Upphandlingar',      desc: 'Bevaka offentliga upphandlingar och ramavtal. Filtrera på kategori, region och värde — missa aldrig en affärsmöjlighet.' },
      { icon: FolderOpenIcon,  label: 'Dokumentbibliotek',  desc: 'Sökbart och sorterbart bibliotek för alla typer av dokument. Kategorier, taggar och direktlänkar — aldrig leta bland gamla e-postbilagor igen.' },
      { icon: WbIncandescentIcon,label: 'Produkter & Tjänster', desc: 'Hantera företagets produkt- och tjänstekatalog. Koppla mot offerter och anbud för konsekventa priser och beskrivningar.' },
      { icon: AccountBalanceWalletIcon, label: 'BudgetN', desc: 'Enhetlig budgethantering för projekt och offerter. Roller, personer, budgetperioder och faktisk utfallsjämförelse — alltid koll på ekonomin.' },
    ],
  },
  {
    category: 'Kommunicera & Synas',
    color: '#CE93D8',
    gradient: 'linear-gradient(135deg, rgba(206,147,216,0.1) 0%, transparent 60%)',
    modules: [
      { icon: TvIcon,       label: 'CastN — Digital skyltning', desc: 'Skapa och schemalägg bildspel för reception, konferensrum och gemensamma skärmar. Hantera displays, slides och casts från ett ställe.' },
      { icon: LinkedInIcon, label: 'LinkedIn-verkstad',         desc: 'Skapa, förhandsgranska och schemalägg LinkedIn-inlägg direkt i LagbasN. AI-förslag på text och hashtags baserat på er profil och målgrupp.' },
      { icon: EmailIcon,    label: 'Mailsignatur',              desc: 'Generera korrekta mailsignaturer automatiskt från medarbetarregistret. Ändrar du telefonnummer syns det direkt — alltid konsekvent och korrekt.' },
      { icon: TrainIcon,    label: 'Avgångstavla',              desc: 'Live-kollektivtrafikavgångar för kontoret. SL-integration med realtidsdata — se nästa pendeltåg, tunnelbana och buss direkt i appen.' },
      { icon: InstallMobileIcon, label: 'PWA — Installera som app', desc: 'LagbasN fungerar som en native app på mobil och desktop. Installera direkt från webbläsaren — inga appbutiker, alltid senaste versionen.' },
    ],
  },
];

// ── AI features ───────────────────────────────────────────────────────────────
const AI_FEATURES = [
  { title: 'Konsultmatchning',   desc: 'Analysera förfrågningsunderlag och matcha mot rätt konsultprofiler automatiskt.' },
  { title: 'CV-anpassning',      desc: 'Anpassa konsultprofiler och kompetenser till specifika anbudskrav med AI-förslag.' },
  { title: 'Offerttexter',       desc: 'Generera övertygande offerttexter baserade på kund, tjänst och referensuppdrag.' },
  { title: 'Workshop-analys',    desc: 'Sammanfatta teman, mönster och åtgärdsförslag från workshopsvar i realtid.' },
  { title: 'SWOT-strategi',      desc: 'Identifiera korskopplingar (SO/ST/WO/WT) och prioriterade strategiska åtgärder.' },
  { title: 'Hybrid-scanning',    desc: 'Extrahera handskriven text från workshopblad och importera till digitalt board.' },
  { title: 'IUP-formulering',    desc: 'AI-assistans vid formulering av individuella utveckingsmål och åtgärder.' },
  { title: 'Gap-analys',         desc: 'Identifiera kompetensgap automatiskt utifrån kravprofil och konsulternas bakgrund.' },
  { title: 'Avtalsextraktion',   desc: 'Ladda upp ett befintligt avtal som PDF — Claude läser ut alla fält automatiskt och fyller i formuläret.' },
];

function ContactSection() {
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await httpsCallable(functions, 'sendContactForm')(form);
      setStatus('sent');
      setForm({ name: '', email: '', company: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      color: 'white',
      '& fieldset': { borderColor: '#222' },
      '&:hover fieldset': { borderColor: '#444' },
      '&.Mui-focused fieldset': { borderColor: ORANGE },
    },
    '& .MuiInputLabel-root': { color: '#666' },
    '& .MuiInputLabel-root.Mui-focused': { color: ORANGE },
  };

  return (
    <Box sx={{ py: 12, bgcolor: '#080808', borderTop: '1px solid #141414' }}>
      <Container maxWidth="md">
        <Typography variant="overline" sx={{ color: ORANGE, letterSpacing: 4, display: 'block', textAlign: 'center', mb: 2 }}>
          KONTAKT
        </Typography>
        <Typography variant="h3" fontWeight={900} textAlign="center"
          sx={{ color: 'white', mb: 2, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.1 }}>
          Intresserad?
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.45)', textAlign: 'center', mb: 6, fontSize: '0.95rem' }}>
          Hör av dig så berättar vi mer och sätter upp ett konto åt dig.
        </Typography>

        {status === 'sent' ? (
          <Box sx={{
            textAlign: 'center', py: 6, px: 4,
            border: '1px solid #1e1e1e', borderRadius: 3,
            background: `linear-gradient(135deg, ${ORANGE}08 0%, transparent 60%)`,
          }}>
            <Typography variant="h5" fontWeight={800} color="white" mb={1.5}>Tack för ditt intresse!</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', lineHeight: 1.8, maxWidth: 400, mx: 'auto' }}>
              Vi har tagit emot din intresseanmälan och återkommer till dig inom kort.
            </Typography>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 560, mx: 'auto' }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Namn *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                required fullWidth sx={fieldSx} />
              <TextField label="Företag" value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                fullWidth sx={fieldSx} />
            </Box>
            <TextField label="E-post *" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              required fullWidth sx={fieldSx} />
            <TextField label="Meddelande *" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
              required fullWidth multiline rows={4} sx={fieldSx} />
            {status === 'error' && (
              <Typography fontSize="0.85rem" color="error">Något gick fel — försök igen eller maila direkt.</Typography>
            )}
            <Button type="submit" variant="contained" disabled={status === 'sending'}
              sx={{
                alignSelf: 'flex-start', bgcolor: ORANGE, color: 'white', fontWeight: 700,
                px: 5, py: 1.5, borderRadius: 2, fontSize: '1rem',
                boxShadow: `0 4px 20px ${ORANGE}44`,
                '&:hover': { bgcolor: '#c94d00' },
                '&.Mui-disabled': { bgcolor: '#333', color: '#666' },
              }}>
              {status === 'sending' ? <CircularProgress size={20} sx={{ color: '#666' }} /> : 'Skicka'}
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default function Landing() {
  const navigate = useNavigate();

  return (
    <Box sx={{
      bgcolor: '#0a0a0a', minHeight: '100vh', color: 'white',
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    }}>

      {/* ── HERO ──────────────────────────────────────────────────────────── */}
      <Box sx={{
        position: 'relative', width: '100%', height: '100vh',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
      }}>
        <Box
          component="video"
          autoPlay loop muted playsInline preload="auto"
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}
        >
          <source src="/lagbasn-hero.webm" type="video/webm" />
          <source src="/lagbasn-hero.mp4"  type="video/mp4" />
        </Box>
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 1,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0.85) 100%)',
        }} />

        {/* Nav */}
        <Box sx={{
          position: 'relative', zIndex: 2,
          px: { xs: 3, md: 5 }, pt: 3,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <Logo size="1.8rem" />
          <Button variant="outlined" onClick={() => navigate('/login')}
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)', backdropFilter: 'blur(4px)',
              '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>
            Logga in
          </Button>
        </Box>

        {/* Hero text */}
        <Box sx={{
          position: 'relative', zIndex: 2,
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          textAlign: 'center', px: { xs: 3, md: 5 },
        }}>
          <Box>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1,
              px: 2, py: 0.75, mb: 3,
              bgcolor: 'rgba(224,85,0,0.15)', border: '1px solid rgba(224,85,0,0.35)',
              borderRadius: 3, backdropFilter: 'blur(8px)',
            }}>
              <AutoAwesomeIcon sx={{ fontSize: 14, color: ORANGE }} />
              <Typography sx={{ fontSize: '0.75rem', color: ORANGE, fontWeight: 700, letterSpacing: 1.5 }}>
                AI-DRIVET AFFÄRSSYSTEM FÖR BYGG & ANLÄGGNING
              </Typography>
            </Box>
            <Typography sx={{
              fontSize: { xs: '2.8rem', sm: '4rem', md: '5.5rem' },
              fontWeight: 900, lineHeight: 1.0, color: 'white', mb: 3,
              letterSpacing: '-2px',
              fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
              textShadow: '0 2px 32px rgba(0,0,0,0.6)',
            }}>
              Ditt lag.<br />
              Din process.<br />
              <Box component="span" sx={{ color: ORANGE }}>Allt på ett ställe.</Box>
            </Typography>
            <Typography variant="h6" sx={{
              color: 'rgba(255,255,255,0.7)',
              mb: 5, fontWeight: 300, lineHeight: 1.7,
              maxWidth: 580, mx: 'auto',
              textShadow: '0 1px 8px rgba(0,0,0,0.5)',
            }}>
              Från konsultprofil och anbud till workshopfacilitering och strategiarbete
              — byggt för konsultbolag som vill vinna mer och leverera bättre.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Button variant="contained" size="large"
                onClick={() => navigate('/login')}
                sx={{
                  bgcolor: ORANGE, color: 'white', fontWeight: 700,
                  px: 4, py: 1.5, fontSize: '1rem', borderRadius: 2,
                  boxShadow: `0 4px 24px ${ORANGE}55`,
                  '&:hover': { bgcolor: '#c94d00' },
                  display: 'flex', alignItems: 'center', gap: 1,
                }}>
                Kom igång <ArrowForwardIcon sx={{ fontSize: 18 }} />
              </Button>
              <Button variant="outlined" size="large" href="#modules"
                sx={{
                  color: 'white', borderColor: 'rgba(255,255,255,0.35)',
                  px: 4, py: 1.5, fontSize: '1rem', borderRadius: 2,
                  backdropFilter: 'blur(4px)',
                  '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.08)' },
                }}>
                Utforska modulerna
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Fade bottom */}
        <Box sx={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: 140, zIndex: 1,
          background: 'linear-gradient(to top, #0a0a0a 0%, transparent 100%)',
          pointerEvents: 'none',
        }} />
      </Box>

      {/* ── STATS BAR ─────────────────────────────────────────────────────── */}
      <Box sx={{ py: 6, bgcolor: '#0d0d0d', borderTop: '1px solid #161616', borderBottom: '1px solid #161616' }}>
        <Container maxWidth="lg">
          <Box sx={{
            display: 'flex', justifyContent: 'center', flexWrap: 'wrap',
            gap: { xs: 4, md: 8 },
          }}>
            {[
              { value: 30, suffix: '+', label: 'Moduler & verktyg' },
              { value: 8,  suffix: '',  label: 'AI-funktioner' },
              { value: 100, suffix: '%', label: 'Webbaserat — inga installationer' },
              { value: 24, suffix: '/7', label: 'Tillgängligt — var du än är' },
            ].map((s, i) => (
              <Box key={i} textAlign="center">
                <Typography sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, fontWeight: 900, color: ORANGE, lineHeight: 1 }}>
                  <Counter to={s.value} suffix={s.suffix} />
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)', mt: 0.5, letterSpacing: 0.5 }}>
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── MODULES ───────────────────────────────────────────────────────── */}
      <Box id="modules" sx={{ py: 14 }}>
        <Container maxWidth="lg">
          <Typography variant="overline" sx={{ color: ORANGE, letterSpacing: 4, display: 'block', textAlign: 'center', mb: 2 }}>
            PLATTFORMEN
          </Typography>
          <Typography variant="h3" fontWeight={900} textAlign="center"
            sx={{ mb: 2, color: 'white', fontFamily: "'DM Sans', sans-serif" }}>
            Allt ett konsultbolag behöver
          </Typography>
          <Typography textAlign="center" sx={{ color: 'rgba(255,255,255,0.55)', mb: 10, fontSize: '1.1rem', maxWidth: 560, mx: 'auto' }}>
            Fem sammanlänkade modulområden. Varje del är kraftfull ensam — men magin uppstår när de samverkar.
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 8 }}>
            {MODULE_GROUPS.map((group, gi) => (
              <Box key={gi}>
                {/* Group header */}
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 2, mb: 3,
                  pb: 2, borderBottom: `1px solid ${group.color}22`,
                }}>
                  <Box sx={{
                    width: 4, height: 28, borderRadius: 2,
                    bgcolor: group.color, flexShrink: 0,
                  }} />
                  <Typography fontWeight={800} fontSize="1.05rem" color={group.color} letterSpacing={0.5}>
                    {group.category}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {group.modules.map((mod, mi) => (
                    <Paper key={mi} sx={{
                      p: 2.5,
                      bgcolor: '#0f0f0f',
                      background: group.gradient,
                      border: `1px solid #1c1c1c`,
                      borderRadius: 2.5,
                      display: 'flex', gap: 2, alignItems: 'flex-start',
                      transition: 'border-color 0.25s, transform 0.2s',
                      '&:hover': {
                        borderColor: group.color + '55',
                        transform: 'translateX(4px)',
                      },
                    }}>
                      <Box sx={{
                        width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
                        bgcolor: group.color + '18',
                        border: `1px solid ${group.color}30`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mt: 0.25,
                      }}>
                        <mod.icon sx={{ fontSize: 18, color: group.color }} />
                      </Box>
                      <Box>
                        <Typography fontWeight={700} color="white" fontSize="0.9rem" mb={0.5}>
                          {mod.label}
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem', lineHeight: 1.7 }}>
                          {mod.desc}
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── AI CALLOUT ────────────────────────────────────────────────────── */}
      <Box sx={{
        py: 14,
        background: 'linear-gradient(135deg, #0a0a0a 0%, #100808 50%, #0a0a0a 100%)',
        borderTop: '1px solid #161616', borderBottom: '1px solid #161616',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Glow */}
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 700, height: 400, borderRadius: '50%',
          background: `radial-gradient(ellipse, ${ORANGE}0a 0%, transparent 70%)`,
          pointerEvents: 'none',
        }} />
        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'center', mb: 2 }}>
            <AutoAwesomeIcon sx={{ color: ORANGE, fontSize: 22 }} />
            <Typography variant="overline" sx={{ color: ORANGE, letterSpacing: 4 }}>CLAUDE AI — HELA VÄGEN</Typography>
          </Box>
          <Typography variant="h3" fontWeight={900} textAlign="center"
            sx={{ mb: 2, color: 'white', fontFamily: "'DM Sans', sans-serif" }}>
            AI som faktiskt hjälper
          </Typography>
          <Typography textAlign="center" sx={{ color: 'rgba(255,255,255,0.55)', mb: 8, fontSize: '1rem', maxWidth: 520, mx: 'auto' }}>
            Inte ett chatfönster du aldrig öppnar. AI är inbäddad direkt i arbetsflödet — där du arbetar, när du behöver det.
          </Typography>

          <Grid container spacing={2.5}>
            {AI_FEATURES.map((f, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Box sx={{
                  p: 2.5, borderRadius: 2,
                  bgcolor: `${ORANGE}08`,
                  border: `1px solid ${ORANGE}20`,
                  height: '100%',
                  transition: 'border-color 0.2s',
                  '&:hover': { borderColor: `${ORANGE}55` },
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 15, color: ORANGE, flexShrink: 0 }} />
                    <Typography fontWeight={700} fontSize="0.85rem" color="white">{f.title}</Typography>
                  </Box>
                  <Typography fontSize="0.78rem" color="#555" lineHeight={1.65}>{f.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Terminal */}
          <Box sx={{
            maxWidth: 660, mx: 'auto', mt: 8,
            bgcolor: '#0d0d0d', border: '1px solid #1e1e1e',
            borderRadius: 2.5, overflow: 'hidden',
          }}>
            <Box sx={{
              px: 2, py: 1, bgcolor: '#141414', borderBottom: '1px solid #1e1e1e',
              display: 'flex', gap: 1, alignItems: 'center',
            }}>
              {['#ff5f56','#ffbd2e','#27c93f'].map(c => (
                <Box key={c} sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: c }} />
              ))}
              <Typography variant="caption" sx={{ color: '#444', ml: 1, fontFamily: 'monospace' }}>
                lagbasn — claude-code
              </Typography>
            </Box>
            <Box sx={{ p: 3, fontFamily: 'monospace', fontSize: '0.8rem' }}>
              <Typography sx={{ color: '#555', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                # Anbud inkommet — matchar konsulter mot krav…
              </Typography>
              <Typography sx={{ color: `${ORANGE}cc`, fontFamily: 'monospace', fontSize: '0.8rem', mt: 1 }}>
                $ lagbasn match --tender="BIM-koordinator Citytunneln 2026"
              </Typography>
              <Typography sx={{ color: '#888', fontFamily: 'monospace', fontSize: '0.8rem', mt: 0.75 }}>
                ✓ Analyserar 847 kompetensrader...
              </Typography>
              <Typography sx={{ color: '#888', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                ✓ Matchar mot krav: BIM Level 2, IFC, Revit, 5+ år anläggning...
              </Typography>
              <Typography sx={{ color: '#888', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                ✓ Identifierar 3 toppkandidater, 2 gap-profiler...
              </Typography>
              <Typography sx={{ color: '#4ade80', fontFamily: 'monospace', fontSize: '0.8rem', mt: 0.75 }}>
                ● Anbudsteam föreslaget → exporterar anpassad PDF
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── WORKSHOP SPOTLIGHT ────────────────────────────────────────────── */}
      <Box sx={{ py: 14, bgcolor: '#080808' }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="overline" sx={{ color: '#42A5F5', letterSpacing: 4, display: 'block', mb: 2 }}>
                WORKSHOPVERKTYG
              </Typography>
              <Typography variant="h3" fontWeight={900}
                sx={{ color: 'white', mb: 3, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.1 }}>
                Workshops som<br />
                <Box component="span" sx={{ color: '#42A5F5' }}>faktiskt leder framåt</Box>
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.9, mb: 3, fontSize: '0.95rem' }}>
                WorkshopN och SwotN är byggda för moderna faciliteringsteam. Kombinera det digitala
                och det fysiska — deltagare svarar via QR på mobilen, eller skriver för hand på
                papper som AI-scannas efteråt.
              </Typography>
              {[
                'Realtidssvar via QR — inga appar, inga konton',
                'Nedräkningstimer med ljud — orange → röd → alarm',
                'Hybridblad: skriv för hand, skanna med AI',
                'Ordmoln och AI-analys efter sessionen',
                'SWOT med AI-korskopplingar (SO/ST/WO/WT)',
                'Dela sessioner via QR-kod-inbjudan',
              ].map((item, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                  <CheckCircleIcon sx={{ fontSize: 15, color: '#42A5F5', flexShrink: 0 }} />
                  <Typography fontSize="0.85rem" color="rgba(255,255,255,0.65)">{item}</Typography>
                </Box>
              ))}
            </Grid>
            <Grid item xs={12} md={6}>
              {/* Mock-up cards */}
              <Box sx={{ position: 'relative' }}>
                {/* WorkshopN card */}
                <Box sx={{
                  p: 3, borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(66,165,245,0.12), rgba(66,165,245,0.04))',
                  border: '1px solid rgba(66,165,245,0.25)',
                  mb: 2,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <GroupsIcon sx={{ color: '#42A5F5', fontSize: 22 }} />
                    <Typography fontWeight={800} color="white">WorkshopN</Typography>
                    <Chip label="Live" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#4CAF5022', color: '#4CAF50', fontWeight: 700, ml: 'auto' }} />
                  </Box>
                  <Typography fontSize="0.78rem" color="#555" mb={1.5}>Fråga 2 av 5: Vad hindrar er idag?</Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {['Resursbrist', 'Kommunikation', 'Verktyg', 'Tid', 'Kompetens'].map((tag, i) => (
                      <Box key={i} sx={{
                        px: 1.25, py: 0.35, borderRadius: 1.5, fontSize: '0.7rem',
                        bgcolor: 'rgba(66,165,245,0.12)', color: '#42A5F5',
                        border: '1px solid rgba(66,165,245,0.2)',
                        fontWeight: 600,
                        opacity: 1 - i * 0.12,
                        transform: `scale(${1 - i * 0.04})`,
                      }}>
                        {tag}
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TimerIcon sx={{ fontSize: 14, color: '#42A5F5' }} />
                    <Typography fontSize="0.7rem" color="#42A5F5" fontWeight={700}>03:42</Typography>
                    <Typography fontSize="0.7rem" color="#555" ml="auto">14 svar inkomna</Typography>
                  </Box>
                </Box>

                {/* SwotN card */}
                <Box sx={{
                  p: 3, borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(224,85,0,0.1), rgba(224,85,0,0.03))',
                  border: `1px solid ${ORANGE}33`,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <GridViewIcon sx={{ color: ORANGE, fontSize: 22 }} />
                    <Typography fontWeight={800} color="white">SwotN</Typography>
                    <Chip label="AI-analys" size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: `${ORANGE}22`, color: ORANGE, fontWeight: 700, ml: 'auto' }} />
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    {[
                      { label: 'S  Styrkor', color: '#4CAF50', items: ['BIM-kompetens', 'Starka relationer'] },
                      { label: 'W  Svagheter', color: '#EF5350', items: ['Digital kapacitet', 'Ledtider'] },
                      { label: 'O  Möjligheter', color: '#42A5F5', items: ['Hållbara projekt', 'AI-effektivisering'] },
                      { label: 'T  Hot', color: ORANGE, items: ['Konkurrens', 'Kompetensbrist'] },
                    ].map((q, i) => (
                      <Box key={i} sx={{
                        p: 1.25, borderRadius: 1.5,
                        bgcolor: q.color + '12',
                        border: `1px solid ${q.color}25`,
                      }}>
                        <Typography fontSize="0.62rem" fontWeight={800} color={q.color} mb={0.5}>{q.label}</Typography>
                        {q.items.map((item, j) => (
                          <Typography key={j} fontSize="0.65rem" color="rgba(255,255,255,0.5)">{item}</Typography>
                        ))}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── OM LAGBASN ────────────────────────────────────────────────────── */}
      <Box sx={{ pt: 8, pb: 14, bgcolor: '#0a0a0a', borderTop: '1px solid #141414' }}>
        <Container maxWidth="lg">
          <Grid container spacing={10} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="overline" sx={{ color: ORANGE, letterSpacing: 4, display: 'block', mb: 2 }}>
                OM LAGBASN
              </Typography>
              <Typography variant="h3" fontWeight={900}
                sx={{ color: 'white', mb: 3, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.1 }}>
                Byggd av konsulter,<br />för konsulter
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.9, mb: 2, fontSize: '0.95rem' }}>
                LagbasN är utvecklad av specialister inom digitala arbetssätt i bygg- och
                anläggningssektorn. Vi vet vad som krävs för att vinna komplexa anbud,
                leda effektiva workshops och hålla koll på en växande organisation.
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.9, fontSize: '0.95rem' }}>
                Rätt konsultprofil i rätt anbud, rätt workshop-format för rätt grupp —
                LagbasN ger dig verktygen att alltid prestera på topp.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{
                bgcolor: '#0f0f0f', border: '1px solid #1a1a1a',
                borderRadius: 3, p: 5, textAlign: 'center',
                position: 'relative', overflow: 'hidden',
              }}>
                <Box sx={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  background: `radial-gradient(circle at center, ${ORANGE}07 0%, transparent 70%)`,
                  pointerEvents: 'none',
                }} />
                <Logo size="3.5rem" />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.2)', mt: 2, letterSpacing: 2, fontSize: '0.65rem' }}>
                  KOMPLETT AFFÄRSSYSTEM FÖR KONSULTBOLAG
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 5 }}>
                  {[
                    { icon: GavelIcon,     label: 'Anbud & Offerter',     color: ORANGE },
                    { icon: GroupsIcon,    label: 'Workshop & SWOT',       color: '#42A5F5' },
                    { icon: AutoAwesomeIcon,label: 'AI-matchning',         color: '#CE93D8' },
                    { icon: PeopleIcon,    label: 'Team & Kompetens',      color: '#4CAF50' },
                    { icon: BusinessIcon,  label: 'CRM & Projekt',         color: '#FF9800' },
                    { icon: TvIcon,        label: 'Kommunikation',         color: '#81D4FA' },
                  ].map((item, i) => (
                    <Box key={i} sx={{
                      display: 'flex', alignItems: 'center', gap: 1.25, p: 1.5,
                      bgcolor: `${item.color}0a`, borderRadius: 1.5,
                      border: `1px solid ${item.color}18`,
                    }}>
                      <item.icon sx={{ fontSize: 16, color: item.color, flexShrink: 0 }} />
                      <Typography fontSize="0.75rem" color="rgba(255,255,255,0.5)" fontWeight={600}>
                        {item.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── TECH STACK ────────────────────────────────────────────────────── */}
      <Box sx={{ py: 10, bgcolor: '#080808', borderTop: '1px solid #141414' }}>
        <Container maxWidth="lg">
          <Typography variant="overline" sx={{ color: '#444', letterSpacing: 4, display: 'block', textAlign: 'center', mb: 1 }}>
            BYGGT MED
          </Typography>
          <Typography variant="h4" fontWeight={900} textAlign="center"
            sx={{ mb: 6, fontFamily: "'DM Sans', sans-serif",
              background: 'linear-gradient(90deg, #61dafb, #a78bfa, #cc785c, #00c7b7)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
            Tech stack
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
            {[
              { label: 'React 18',         color: '#61dafb' },
              { label: 'Firebase',         color: '#ffca28' },
              { label: 'Netlify CI/CD',    color: '#00c7b7' },
              { label: 'Claude AI',        color: '#cc785c' },
              { label: 'Material UI v5',   color: '#007fff' },
              { label: 'Microsoft SSO',    color: '#00a4ef' },
              { label: 'Claude Code',      color: '#a78bfa' },
              { label: 'SheetJS',          color: '#4ade80' },
              { label: 'jsPDF',            color: ORANGE },
              { label: 'QRCode',           color: '#f97316' },
              { label: 'Anthropic API',    color: '#cc785c' },
              { label: 'Firebase Storage', color: '#ffca28' },
            ].map(b => (
              <Chip key={b.label} label={b.label} size="small" sx={{
                bgcolor: b.color + '10', color: b.color,
                border: `1px solid ${b.color}25`,
                fontFamily: 'monospace', fontSize: '0.72rem', letterSpacing: 0.3,
              }} />
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── KONTAKT ───────────────────────────────────────────────────────── */}
      <ContactSection />

      {/* ── CTA ───────────────────────────────────────────────────────────── */}
      <Box sx={{
        py: 16,
        background: `linear-gradient(135deg, #0f0f0f 0%, #0d0508 50%, #0f0f0f 100%)`,
        textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 800, height: 500, borderRadius: '50%',
          background: `radial-gradient(ellipse, ${ORANGE}08 0%, transparent 70%)`,
          pointerEvents: 'none',
        }} />
        <Container maxWidth="md" sx={{ position: 'relative' }}>
          <Typography sx={{
            fontSize: { xs: '2.2rem', md: '3.5rem' }, fontWeight: 900,
            color: 'white', mb: 2, fontFamily: "'DM Sans', sans-serif",
            lineHeight: 1.1,
          }}>
            Redo att ta nästa steg?
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.45)', mb: 6, lineHeight: 1.6 }}>
            Vinn fler anbud. Facilitera bättre workshops.<br />Bygg ett starkare team.
          </Typography>
          <Button variant="contained" size="large"
            onClick={() => navigate('/login')}
            sx={{
              bgcolor: ORANGE, color: 'white', fontWeight: 700,
              px: 6, py: 2, fontSize: '1.1rem', borderRadius: 2,
              boxShadow: `0 8px 32px ${ORANGE}44`,
              '&:hover': { bgcolor: '#c94d00' },
              display: 'inline-flex', alignItems: 'center', gap: 1,
            }}>
            Logga in och kom igång <ArrowForwardIcon />
          </Button>
        </Container>
      </Box>

      {/* ── FOOTER ────────────────────────────────────────────────────────── */}
      <Box sx={{ py: 4, bgcolor: '#050505', textAlign: 'center', borderTop: '1px solid #111' }}>
        <Logo size="1.2rem" />
        <Typography variant="caption" sx={{ color: '#444', display: 'block', mt: 1 }}>
          Utvecklad med ☕ och Claude Code i Stockholm
        </Typography>
        <Typography variant="caption" sx={{ color: '#333', display: 'block', mt: 0.5, fontFamily: 'monospace' }}>
          v{pkg.version}
        </Typography>
      </Box>
    </Box>
  );
}
